import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Ticket, Plus, Trash2, Pencil } from "lucide-react";

const empty = {
  code: "", discount_percent: 0, discount_flat: 0, min_amount: 0, max_discount: 0,
  usage_limit: 0, is_active: true, description: "",
  applies_to: "all", service_slugs: [],
};

export default function AdminCoupons() {
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/coupons").then((r) => setRows(r.data.coupons));
  const loadServices = () => api.get("/services").then((r) => setServices(r.data.services || []));

  useEffect(() => { load(); loadServices(); }, []);

  const submit = async () => {
    try {
      const body = {
        ...form,
        code: form.code.toUpperCase().trim(),
        applies_to: form.applies_to || "all",
        service_slugs: form.applies_to === "specific" ? (form.service_slugs || []) : [],
      };
      if (body.applies_to === "specific" && body.service_slugs.length === 0) {
        toast.error("Select at least one service for a specific-services coupon");
        return;
      }
      if (editing) await api.put(`/admin/coupons/${editing}`, body);
      else await api.post(`/admin/coupons`, body);
      toast.success("Saved"); setOpen(false); setForm(empty); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await api.delete(`/admin/coupons/${id}`); toast.success("Deleted"); load();
  };

  const toggleServiceSlug = (slug) => {
    const set = new Set(form.service_slugs || []);
    if (set.has(slug)) set.delete(slug);
    else set.add(slug);
    setForm({ ...form, service_slugs: Array.from(set) });
  };

  return (
    <DashboardLayout admin>
      <div className="mb-6 flex items-center justify-between">
        <div><div className="section-label">Marketing</div><h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]">Coupons</h1></div>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="coupon-new-btn"><Plus className="w-4 h-4 mr-1" />New coupon</Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="p-3 text-left">Code</th>
              <th className="p-3">% Off</th>
              <th className="p-3">Flat ₹</th>
              <th className="p-3">Applies To</th>
              <th className="p-3">Min ₹</th>
              <th className="p-3">Max ₹</th>
              <th className="p-3">Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`coupon-row-${c.code}`}>
                <td className="p-3 font-mono">{c.code}<div className="text-xs text-slate-500">{c.description}</div></td>
                <td className="p-3 text-center">{c.discount_percent || "-"}</td>
                <td className="p-3 text-center">{c.discount_flat || "-"}</td>
                <td className="p-3 text-center text-xs">
                  {c.applies_to === "specific"
                    ? <span title={(c.service_slugs || []).join(", ")}>{(c.service_slugs || []).length} service{(c.service_slugs || []).length === 1 ? "" : "s"}</span>
                    : <span className="text-emerald-700 font-medium">All services</span>}
                </td>
                <td className="p-3 text-center">{c.min_amount}</td>
                <td className="p-3 text-center">{c.max_discount || "-"}</td>
                <td className="p-3 text-center">{c.is_active ? "Yes" : "No"}</td>
                <td className="p-3 flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => { setEditing(c.id); setForm({ ...empty, ...c, applies_to: c.applies_to || "all", service_slugs: c.service_slugs || [] }); setOpen(true); }} data-testid={`coupon-edit-${c.code}`}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(c.id)} data-testid={`coupon-del-${c.code}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-500"><Ticket className="w-8 h-8 mx-auto mb-2" />No coupons yet</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="coupon-form-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} coupon</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="coupon-form-code" /></div>
            <div><Label>Discount %</Label><Input type="number" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: parseFloat(e.target.value) || 0 })} data-testid="coupon-form-percent" /></div>
            <div><Label>Discount ₹ (flat)</Label><Input type="number" value={form.discount_flat} onChange={(e) => setForm({ ...form, discount_flat: parseFloat(e.target.value) || 0 })} data-testid="coupon-form-flat" /></div>
            <div><Label>Min amount ₹</Label><Input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Max discount ₹ cap</Label><Input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Usage limit (0 = ∞)</Label><Input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: parseInt(e.target.value) || 0 })} /></div>
            <div className="flex items-end gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="coupon-form-active" /><span className="text-sm">Active</span></div>
            <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Independence Day 15% off" /></div>

            {/* Applies To */}
            <div className="col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <Label className="text-xs uppercase tracking-widest text-slate-500">Apply To</Label>
              <div className="mt-2 flex items-center gap-4 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="cf-applies-to"
                    checked={form.applies_to === "all"}
                    onChange={() => setForm({ ...form, applies_to: "all", service_slugs: [] })}
                    data-testid="coupon-form-applies-all"
                  />
                  All services
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="cf-applies-to"
                    checked={form.applies_to === "specific"}
                    onChange={() => setForm({ ...form, applies_to: "specific" })}
                    data-testid="coupon-form-applies-specific"
                  />
                  Specific services
                </label>
              </div>
              {form.applies_to === "specific" && (
                <div className="mt-3">
                  <div className="text-xs text-slate-500 mb-2">Select the services this coupon can be used on. Customers using it on other services will see “This coupon is not valid for this service”.</div>
                  <div className="max-h-56 overflow-y-auto rounded border border-slate-200 bg-white p-2 space-y-1" data-testid="coupon-form-services-list">
                    {services.length === 0 && <div className="text-xs text-slate-400 p-2">Loading services…</div>}
                    {services.map((s) => {
                      const checked = (form.service_slugs || []).includes(s.slug);
                      return (
                        <label
                          key={s.slug}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm hover:bg-slate-50 ${checked ? "bg-[#1E3A8A]/5" : ""}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleServiceSlug(s.slug)}
                            data-testid={`coupon-form-svc-${s.slug}`}
                          />
                          <span className="flex-1">{s.name}</span>
                          <span className="text-xs text-slate-400 font-mono">{s.slug}</span>
                        </label>
                      );
                    })}
                  </div>
                  {(form.service_slugs || []).length > 0 && (
                    <div className="text-xs text-slate-500 mt-2">{form.service_slugs.length} selected</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} disabled={!form.code} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="coupon-form-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
